// © Microsoft Corporation. All rights reserved.

import { HeadingProps } from "@microsoft/fast-components-react-msft";

/**
 * An enum for intra article component position values
 *
 * @export
 * @enum IntraArticleComponentPosition
 */
export enum IntraArticleComponentPosition {
    Normal = "normal",
    Wide = "wide"
}

/**
 * An enum for views article component types
 *
 * @export
 * @enum IntraArticleComponentType
 */
export enum IntraArticleComponentType {
    ArticleReader = "articlereader",
    InlineImage = "inlineimage",
    InlineSlideshow = "inlineslideshow",
    InlineVideo = "inlinevideo",
    FacebookEmbed = "facebookembed",
    TwitterEmbed = "twitterembed",
    InstagramEmbed = "instagramembed",
    GiphyEmbed = "giphyembed",
    GraphiqEmbed = "graphiqembed",
    PinterestEmbed = "pinterestembed",
    TextParagraph = "textparagraph",
    Unknown = "unknown"
}

/**
 * An interface for SocialEmbedData contract
 *
 * @export
 * @interface SocialEmbedData
 */
export interface SocialEmbedData {
    /**
     * Social feed attribution
     * @type {string}
     */
    attribution?: string;

    /**
     * Url to fetch social feed from provider
     * @type {string}
     */
    feedHref: string;

    /**
     * Embed identifier
     * @type {string}
     */
    id: string;

    /**
     * Title for the social feed
     * @type {string}
     */
    title?: string;

    /**
     * Social embed type
     * @type {IntraArticleComponentType}
     */
    type: IntraArticleComponentType;
}

/**
 * An interface for IntraArticleComponent contract
 *
 * @export
 * @interface IntraArticleComponent
 */
export interface IntraArticleComponent {
    /**
     * CMS document id
     * @type {string}
     */
    cmsDocumentId?: string;

    /**
     * Additional component data
     * @type {unknown}
     */
    data?: unknown;

    /**
     * Message to show if error encountered while loading component
     * @type {HeadingProps}
     */
    loadError?: HeadingProps;

    /**
     * Ingested component markup
     * @type {string}
     */
    marker: string;

    /**
     * Position index in ingested article body markup
     * @type {number}
     */
    startIndex: number;

    /**
     * Expected component positioning inside article body
     * @type {IntraArticleComponentPosition}
     */
    position?: IntraArticleComponentPosition;

    /**
     * Type of component
     * @type {IntraArticleComponentType}
     */
    type: IntraArticleComponentType;
}
